import { useState, useEffect, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import Header from "../../../components/Header/header";
import RouteList from "./RouteList";
import ImageSidebar from "../../../components/Header/ImageSidebar";
import { isMobile } from "react-device-detect";
import Account from "../Account/account";
import {
  fetchUser,
  updateFirstRedirect,
  updatePageReady,
} from "../../../Utils/SupabaseServices/UserDatabase";
import AnalyticsTabs from "../Analytics/analyticsTab";
import { TabTitle } from "../../../Utils/GeneralFunctions";
import ControlsTab from "../Controls/controlsTab";
import DashboardSkeleton from "../../../components/skeleton/DashboardView/dashboardSkeleton";
import CopyToClipboard from "../../../components/CopyToClipboard/CopyToClipboard";
import track, { TRACKING_TYPES } from "../../../Utils/SupabaseServices/Track";
import { useAuth } from "../../../hooks/useAuth";
import { useLoadUser } from "../../../Utils/customHooks/useLoadUser";
import ManageLinks from "../ManageLinks/ManageLinks";
import StayUpToDateControls from "../StayUpToDate/StayUpToDateControls";
import ProductBank from "../ProductBank/ProductBank";
import FeedNew from "../Feed new/FeedNew";
import SocialIntegration from "../SocialIntegration/SocialIntegration";
import { useLoadProfileSetup } from "../../../Utils/customHooks/useLoadProfileSetup";
import "./Dashboard.css";
import Sidebar from "./Sidebar";
import { BannerSkeleton } from "../../../components/StayUpToDateComponent/BannerSkeleton";
import { SkeletonStatBox } from "../Analytics/Overview/OverviewSmoothCharts";
import EmbedSearch from "../EmbedSearch/EmbedSearch";
import SlidingSidebar from "../../../components/SlidingSidebar/SlidingSidebar";
import { useSidebarState } from "../../../context/SidebarStateContext";
import StayUpToDateSkeleton from "../../../components/skeleton/DashboardView/dashboardSkeleton";
import WelcomeConfetti from "../../../components/Confetti/WelcomeConfetti";
import FirstLoginModal from "../../../components/Modals/FirstLoginModal";
import { useProductFruitsApi } from "react-product-fruits";
import Tutorials from "../Tutorials/Tutorials";
import DashboardReadyModal from "../../../components/Modals/DashboardReadyModal";

const Dashboard = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const user_id = user?.id || "";
  const location = useLocation();
  const { state } = location;
  const queryClient = useQueryClient();
  const [showConfetti, setShowConfetti] = useState(false);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [showReadyModal, setShowReadyModal] = useState(false);

  TabTitle(`${username} • Search Bubble`);
  const {
    handleClick,
    isEmail,
    isName,
    isPhone,
    isPhoneEmpty,
    tab,
    isEmailVerified,
    option,
    setIsEmail,
    setIsName,
    setIsPhone,
    setIsPhoneEmpty,
    setTab,
    setIsEmailVerified,
  } = useSidebarState();

  // useEffect(() => {
  //   const handleStorageChange = () => {
  //     const emailData = localStorage.getItem("email_verify");
  //     const phoneData = localStorage.getItem("phone_verify");
  //     if (location.pathname.includes("productbank")) {
  //       handleClick("Product Bank");
  //     } else {
  //       // Check if either email_verify or phone_verify is "false"
  //       if (emailData === "false" || phoneData === "false") {
  //         // setOption("Account");
  //       } else if (!emailData && !phoneData) {
  //         // Check if both email_verify and phone_verify are not present or have truthy values
  //         // setOption("Analytics");
  //       }
  //     }
  //   };

  //   // Add event listener for storage changes
  //   window.addEventListener("storage", handleStorageChange);

  //   // Initial setup (run once)
  //   handleStorageChange();

  //   // Clean up the event listener when the component unmounts
  //   return () => {
  //     window.removeEventListener("storage", handleStorageChange);
  //   };
  // }, [window]);

  // const [isEmail, setIsEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  // const [isName, setIsName] = useState(false);
  // const [isPhone, setIsPhone] = useState(false);
  // const [isPhoneEmpty, setIsPhoneEmpty] = useState(false);
  // const [tab, setTab] = useState<any>();
  // const [isEmailVerified, setIsEmailVerified] = useState<any>();

  useEffect(() => {
    fetchEmail();
  }, [isEmail, isName]);

  useEffect(() => {
    track(location.pathname, TRACKING_TYPES.PAGE, {
      pathname: location.pathname,
      title: "Dashboard",
      user_id: user_id,
    });
  }, []);

  const {
    isLoading: userLoading,
    data: userData,
    refetch,
  }: any = useLoadUser(username);
  const {
    isLoading,
    data: creatorData,
    error,
    refetch: refetchUserDetail,
  }: any = useLoadProfileSetup(username, "preview");

  // Add useEffect to refetch data when switching to Landing Page
  useEffect(() => {
    if (option && (option.includes("Landing Page") || option.includes("Landing"))) {
      // Refetch user data and user detail when switching to landing page
      refetch();
      refetchUserDetail();
      
      // Invalidate relevant query caches to ensure fresh data
      queryClient.invalidateQueries(["StayUpTpDateComponents", user_id]);
      queryClient.invalidateQueries(["fetchAllData", user_id]);
      queryClient.invalidateQueries(["fetchProfileSetup", username]);
      queryClient.invalidateQueries(["customContentMaxCount", user_id]);
      queryClient.invalidateQueries(["customContent", user_id]);
      queryClient.invalidateQueries(["paginatedQueryFeed"]);
      queryClient.invalidateQueries(["getComponent", user_id]);
      
      // Invalidate featured content queries
      queryClient.invalidateQueries(["typeContentPosts", user_id, "FEATURE"]);
      queryClient.invalidateQueries(["postIdsFeed", user_id]);
      queryClient.invalidateQueries(["paginatedQueryFeedMaxCount"]);
      
      // Invalidate feed content queries that might include featured content
      queryClient.invalidateQueries(["paginatedPosts"]);
      queryClient.invalidateQueries(["feedPosts"]);
      queryClient.invalidateQueries(["getFeedPageCount"]);
      queryClient.invalidateQueries(["getLoadEmbedPages"]);
      queryClient.invalidateQueries(["QueryBasedFeed"]);
      queryClient.invalidateQueries(["totalProductBankCount"]);
      
      // Invalidate any queries that filter by "active,featured" index
      queryClient.invalidateQueries(["adPosts"]);
      queryClient.invalidateQueries(["necFeed"]);
      
      // Force refetch after a short delay to ensure cache is cleared
      setTimeout(() => {
        refetch();
        refetchUserDetail();
      }, 100);
    }
  }, [option, refetch, refetchUserDetail, queryClient, user_id, username]);

  const fetchEmail = useCallback(async () => {
    try {
      const userData = await fetchUser(username);
      if (!userData) return;

      const hasEmail = userData.email !== "";
      const hasName = userData.name !== "";
      const emailVerified = userData.email_verify;

      if (hasEmail && hasName) {
        setIsName(true);
        setIsEmail(true);
        setIsPhone(true);
        setIsEmailVerified(emailVerified);
        if (!emailVerified) {
          handleClick("Account");
        }
      } else {
        if (!emailVerified) {
          handleClick("Account");
        } else {
          handleClick("Landing Page");
        }
        setIsEmail(hasEmail);
        setIsName(hasName);
        // handleClick("Landing Page");
        // setTab(0);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    // Show confetti only if coming from onboarding
    const isFromOnboarding =
      state?.from === "onboarding" && userData?.is_first_redirect;

    // Show first login modal if user has is_first_login true in database
    // Check for both boolean true and string "true"
    const isFirstLogin =
      state?.from === "login" &&
      (userData?.is_first_login === true ||
        userData?.is_first_login === "true");
    const isPageReady =
      userData?.platforms_data &&
      (userData?.is_first_login === "false" ||
        userData?.is_first_login === false) &&
      (userData?.is_page_ready === true || userData?.is_page_ready === "true");
    if (isFromOnboarding) {
      setShowConfetti(true);
      // Update first_redirect flag after showing confetti
      if (username) {
        updateFirstRedirect(username);
      }
    } else if (isFirstLogin) {
      setShowFirstLoginModal(true);
    } else if (isPageReady) {
      setShowReadyModal(true);
      if (username) {
        updatePageReady(username);
      }
    }
  }, [state, userData, username]);

  // Implement ProductFruits announcements with proper useEffect and dependencies
  // useEffect(() => {
  // Only show announcements if user is logged in and dashboard has loaded
  // if (!loading && userData) {
  // useProductFruitsApi((api) => {
  //   api.announcements.showById("3f6184af-c132-4b4b-9d3e-50b3a3aee3fd");
  // }, []);
  // }
  // }, [loading, userData]);
  // useEffect(() => {
  //   // useProductFruitsApi((api) => {
  //     api.announcements.showById("3f6184af-c132-4b4b-9d3e-50b3a3aee3fd");
  //   // }, []);
  // }, []);
  return (
    <div className="dashboard">
      {showConfetti && username && <WelcomeConfetti username={username} />}
      {showFirstLoginModal && username && (
        <FirstLoginModal
          username={username}
          onClose={() => setShowFirstLoginModal(false)}
        />
      )}
      {showReadyModal && (
        <DashboardReadyModal
          username={username}
          onClose={() => setShowReadyModal(false)}
        />
      )}
      <div className="dashboard-layout">
        <Sidebar
          username={username}
          pictureUrl={userData?.picture}
          email_verify={userData?.email_verify}
          userData={userData}
        />

        <div className="content-container">
          <div className="row">
            <h1 className="dashboard-mobile-show">Dashboard</h1>
            {loading ? (
              ""
            ) : (
              <>
                <div className="right-panel box">
                  {option ? (
                    option === "Account" ? (
                      <Account
                        username={username}
                        setIsEmail={setIsEmail}
                        setIsName={setIsName}
                        setIsPhone={setIsPhone}
                        setIsPhoneEmpty={setIsPhoneEmpty}
                        setIsEmailVerified={setIsEmailVerified}
                        subscription_type={userData?.subscription_type}
                      />
                    ) : option === "Tutorials" ? (
                      <Tutorials />
                    ) : option === "Analytics" ? (
                      <AnalyticsTabs
                        username={username}
                        user_id={user_id}
                        subscription_type={userData?.subscription_type}
                        brand_color={userData?.brand_color}
                        showSocials={
                          creatorData?.combinedRes?.userData?.showSocials
                        }
                        is_embed_search={userData?.is_embed_search}
                        embed_configure={userData?.embed_configure}
                        // embedComponents={userData?.embed_components}
                      />
                    ) : option === "Connected Platforms" ? (
                      <SocialIntegration
                        username={username}
                        user_id={userData}
                      />
                    ) : option === "Analytics" ? (
                      <AnalyticsTabs
                        username={username}
                        user_id={user_id}
                        subscription_type={userData?.subscription_type}
                        brand_color={userData?.brand_color}
                        showSocials={
                          creatorData?.combinedRes?.userData?.showSocials
                        }
                        is_embed_search={userData?.is_embed_search}
                        embed_configure={userData?.embed_configure}
                      />
                    ) : option.includes("Controls") ? (
                      <ControlsTab
                        refetch={refetch}
                        subscription_type={userData?.subscription_type}
                      />
                    ) : option.includes("Content") ? (
                      <FeedNew
                        subscription_type={userData?.subscription_type}
                      />
                    ) : option.includes("Links") ? (
                      <ManageLinks username={username} />
                    ) : option.includes("Landing Page") ? (
                      <StayUpToDateControls
                        username={username}
                        showSocials={userData?.showSocials}
                        myLinks={userData?.myLinks}
                        subscription_type={userData?.subscription_type}
                        user_id={user_id}
                        brand_color={userData?.brand_color}
                        isFlowdeskOn={userData?.is_flowdesk_on}
                        flowdeskId={userData?.flowdeskId}
                        isFlowdeskPopupOn={userData?.is_flowdeskpopup_on}
                        flowdeskPopupId={userData?.flowdeskPopupId}
                        isFilloutOn={userData?.is_fillout_on}
                        filloutId={userData?.filloutId}
                        isLandingPage={true}
                        refetchUser={refetch}
                        refetchUserDetail={refetchUserDetail}
                        creatorData={creatorData}
                        isLoading={isLoading}
                        from="landing-page"
                        is_first_redirect={
                          userData?.is_first_redirect ||
                          (state?.is_first_redirect &&
                            state?.from === "onboarding")
                        }
                      />
                    ) : option.includes("Landing") ? (
                      <StayUpToDateControls
                        username={username}
                        showSocials={userData?.showSocials}
                        myLinks={userData?.myLinks}
                        subscription_type={userData?.subscription_type}
                        user_id={user_id}
                        brand_color={userData?.brand_color}
                        isFlowdeskOn={userData?.is_flowdesk_on}
                        flowdeskId={userData?.flowdeskId}
                        isFlowdeskPopupOn={userData?.is_flowdeskpopup_on}
                        flowdeskPopupId={userData?.flowdeskPopupId}
                        isFilloutOn={userData?.is_fillout_on}
                        filloutId={userData?.filloutId}
                        isLandingPage={false}
                        refetchUser={refetch}
                        refetchUserDetail={refetchUserDetail}
                        creatorData={creatorData}
                        isLoading={isLoading}
                        is_first_redirect={
                          userData?.is_first_redirect ||
                          (state?.is_first_redirect &&
                            state?.from === "onboarding")
                        }
                      />
                    ) : option.includes("Product Bank") ? (
                      <ProductBank
                        username={username}
                        showSocials={userData?.showSocials}
                        myLinks={userData?.myLinks}
                        subscription_type={userData?.subscription_type}
                        user_id={user_id}
                        brand_color={userData?.brand_color}
                      />
                    ) : option === "Embed Page" ? (
                      <StayUpToDateControls
                        username={username}
                        showSocials={userData?.showSocials}
                        myLinks={userData?.myLinks}
                        subscription_type={userData?.subscription_type}
                        user_id={user_id}
                        brand_color={userData?.brand_color}
                        isFlowdeskOn={userData?.is_flowdesk_on}
                        flowdeskId={userData?.flowdeskId}
                        isFlowdeskPopupOn={userData?.is_flowdeskpopup_on}
                        flowdeskPopupId={userData?.flowdeskPopupId}
                        isFilloutOn={userData?.is_fillout_on}
                        filloutId={userData?.filloutId}
                        isLandingPage={false}
                        refetchUser={refetch}
                        refetchUserDetail={refetchUserDetail}
                        creatorData={creatorData}
                        isLoading={isLoading}
                        from="embed-search"
                        is_first_redirect={
                          userData?.is_first_redirect ||
                          (state?.is_first_redirect &&
                            state?.from === "onboarding")
                        }
                      />
                    ) : (
                      ""
                    )
                  ) : (
                    ""
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
